import {
  ColumnFiltersState,
  PaginationState,
  Row,
  RowModel,
} from '@tanstack/react-table'
import { message } from 'antd'
import { useGeckoAnalytics } from 'features/analytics'
import {
  DocumentTableColumns,
  DocumentTableRow,
  SemanticSearchColumns,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { useDocumentSemanticSearch } from 'features/knowledgeDocumentViewer/hooks/useDocumentSemanticSearch'
import { SearchMode } from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

export interface UseAdvancedSearchProps {
  advancedSearchTableData: DocumentTableRow[]
  siteId?: string
  initialGlobalFilter?: string
  initialColumnFilters?: ColumnFiltersState
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  onSelectDocument: (documentId: string) => void
  onClose?: () => void
  searchMode: SearchMode
  setSearchMode: React.Dispatch<React.SetStateAction<SearchMode>>
  searchInputValue: string
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>
  semanticQuery: string
  setSemanticQuery: React.Dispatch<React.SetStateAction<string>>
  columnFilters: ColumnFiltersState
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
  paginatedKeywordData?: DocumentTableRow[]
  paginatedKeywordTotalCount?: number
  isLoadingPaginatedData?: boolean
}

export function useAdvancedSearch({
  advancedSearchTableData,
  siteId,
  initialGlobalFilter = '',
  setPagination,
  onSelectDocument,
  onClose,
  searchMode,
  setSearchMode,
  setSearchInputValue,
  semanticQuery,
  setSemanticQuery,
  setColumnFilters,
  paginatedKeywordData = [],
  paginatedKeywordTotalCount = 0,
  isLoadingPaginatedData = false,
}: UseAdvancedSearchProps) {
  const [feedbackSelectedIds, setFeedbackSelectedIds] = useState<Set<string>>(
    () => new Set()
  )

  const { trackEvent } = useGeckoAnalytics()

  const {
    searchResults: semanticSearchResults,
    isLoading: isSemanticLoading,
    search: executeSemanticSearch,
    clearResults: clearSemanticResults,
    config: semanticSearchConfig,
  } = useDocumentSemanticSearch(siteId ?? '', advancedSearchTableData)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchInputValue(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const handleSemanticQueryChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSemanticQuery(e.target.value)
  }

  const handleSemanticSearch = () => {
    if (semanticQuery.trim()) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      executeSemanticSearch(semanticQuery)
    }
  }

  const handleSemanticKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSemanticSearch()
    }
  }

  const handleFeedbackToggle = useCallback((id: string) => {
    setFeedbackSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleSubmitFeedback = useCallback(() => {
    const allResults = semanticSearchResults.map((doc) => ({
      document_id: doc.id,
      title: doc.title,
    }))

    const relevantDocuments = semanticSearchResults
      .map((doc, index) => ({ doc, rank: index + 1 }))
      .filter(({ doc }) => feedbackSelectedIds.has(doc.id))
      .map(({ doc, rank }) => ({
        document_id: doc.id,
        rank,
      }))

    trackEvent('Semantic Search Feedback Submitted', {
      query: semanticQuery,
      site_id: siteId,
      all_results: allResults,
      relevant_documents: relevantDocuments,
      config: semanticSearchConfig,
    })

    message.success('Feedback submitted successfully')
    setFeedbackSelectedIds(new Set())
  }, [
    semanticSearchResults,
    feedbackSelectedIds,
    trackEvent,
    semanticQuery,
    siteId,
    semanticSearchConfig,
  ])

  const handleSearchModeChange = (value: SearchMode) => {
    setSearchMode(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    if (value === 'keyword') {
      clearSemanticResults()
    }
  }

  const handleDocumentSelect = useCallback(
    (document: DocumentTableRow) => {
      onSelectDocument(document.id)
      onClose?.()
    },
    [onSelectDocument, onClose]
  )

  const handleRowClick = (row: Row<DocumentTableRow>) => {
    handleDocumentSelect(row.original)
  }

  const handleClose = () => {
    onClose?.()
  }

  const handleFilteredDataChange = useCallback(
    (
      filteredData: RowModel<DocumentTableRow>,
      _globalFilter: unknown,
      newColumnFilters: ColumnFiltersState
    ) => {
      setColumnFilters(newColumnFilters)
    },
    [setColumnFilters]
  )

  useEffect(() => {
    setSearchInputValue(initialGlobalFilter)
  }, [initialGlobalFilter, setSearchInputValue])

  useEffect(() => {
    setFeedbackSelectedIds(new Set())
  }, [semanticSearchResults])

  const tableColumns = useMemo(() => {
    if (searchMode === 'semantic') {
      return SemanticSearchColumns({
        feedbackSelectedIds,
        onFeedbackToggle: handleFeedbackToggle,
      })
    }
    return DocumentTableColumns()
  }, [searchMode, feedbackSelectedIds, handleFeedbackToggle])

  const tableData =
    searchMode === 'semantic' ? semanticSearchResults : paginatedKeywordData

  const showSemanticSearchEmptyState =
    searchMode === 'semantic' &&
    !isSemanticLoading &&
    semanticSearchResults.length === 0 &&
    semanticQuery.trim() !== ''

  return {
    feedbackSelectedIds,
    isSemanticLoading,
    isLoadingPaginatedData,
    showSemanticSearchEmptyState,
    tableData,
    tableColumns,
    paginatedKeywordTotalCount,

    handleSearchChange,
    handleSemanticQueryChange,
    handleSemanticKeyDown,
    handleSemanticSearch,
    handleSearchModeChange,
    handleFeedbackToggle,
    handleSubmitFeedback,
    handleRowClick,
    handleDocumentSelect,
    handleFilteredDataChange,
    handleClose,
  }
}
